/**
 * ENG-8272 — the Grok Voice roster, and the pure rules for merging xAI's live
 * list into it.
 *
 * This list used to live only in `packages/webapp/src/lib/grok-voices.ts` and
 * was hand-transcribed twice: the original five (xAI had no list endpoint when
 * they were written), then +21 for Think Fast 2.0 in PR #3899. The failure mode
 * was quiet — a customer read a voice out of xAI's docs, could not find it in
 * our selector, and nothing anywhere logged a discrepancy.
 *
 * xAI now serves `GET /v1/tts/voices`, so the roster can be read. It lives in
 * core because BOTH sides need the same 26: the API's fetcher needs them as its
 * fallback, and the webapp needs them as the value it renders before (and
 * instead of) a fetch. Two copies of a fallback list is the same defect one
 * level up.
 *
 * What stays local, deliberately: the LABELS and the ORDER. xAI describes
 * voices by use case (support, narration, sales, audiobooks) rather than by
 * voice character, so its `name` is not the string we want in a picker, and its
 * ordering is not ours. The fetched roster contributes IDS; the overlay below
 * contributes presentation.
 *
 * NOT in scope here: the model pin. `GROK_VOICE_DEFAULT_MODEL` stays an
 * explicit version in `types/channel-config.ts` and must not become live-read —
 * a new voice is opt-in per agent, whereas a model flip reprices every session
 * with no deploy and no review.
 */

export interface GrokVoiceOption {
  id: string;
  label: string;
}

/** The mint's server-side default when an agent has no configured voice. */
export const GROK_VOICE_DEFAULT = 'eve';

/**
 * Voice ids are the lowercase form of the display name, and this is the exact
 * string handed to xAI as `voice` on `session.update`.
 *
 * A wrongly-cased or malformed id does not fail a typecheck and does not fail a
 * render — it fails at call time, per voice, for whoever picked it. So ids that
 * do not match are dropped from a fetched roster rather than shown.
 */
export const GROK_VOICE_ID_PATTERN = /^[a-z0-9]+$/;

// The original five, kept first: `eve` is the default and these are the voices
// existing agents are already configured with. xAI retrained them alongside the
// 2.0 release (more natural pacing/phrasing) — same ids, so nothing to migrate.
const GROK_VOICE_ORIGINAL: readonly GrokVoiceOption[] = [
  { id: 'eve', label: 'Eve (Female)' },
  { id: 'ara', label: 'Ara (Female)' },
  { id: 'rex', label: 'Rex (Male)' },
  { id: 'sal', label: 'Sal (Neutral)' },
  { id: 'leo', label: 'Leo (Male)' },
];

// The 21 voices added with the Grok Voice Think Fast 2.0 release (2026-07-29),
// in the order xAI published them. No gender/accent label here on purpose: xAI
// documents these by use case rather than by voice character, so inventing a
// gender tag would be us guessing.
const GROK_VOICE_ADDED_2026_07: readonly GrokVoiceOption[] = [
  { id: 'carina', label: 'Carina' },
  { id: 'zagan', label: 'Zagan' },
  { id: 'helix', label: 'Helix' },
  { id: 'orion', label: 'Orion' },
  { id: 'luna', label: 'Luna' },
  { id: 'iris', label: 'Iris' },
  { id: 'altair', label: 'Altair' },
  { id: 'zenith', label: 'Zenith' },
  { id: 'perseus', label: 'Perseus' },
  { id: 'helios', label: 'Helios' },
  { id: 'lux', label: 'Lux' },
  { id: 'kepler', label: 'Kepler' },
  { id: 'rigel', label: 'Rigel' },
  { id: 'cosmo', label: 'Cosmo' },
  { id: 'celeste', label: 'Celeste' },
  { id: 'ursa', label: 'Ursa' },
  { id: 'sirius', label: 'Sirius' },
  { id: 'lumen', label: 'Lumen' },
  { id: 'castor', label: 'Castor' },
  { id: 'naksh', label: 'Naksh' },
  { id: 'atlas', label: 'Atlas' },
];

/**
 * The static roster: what we know without asking xAI.
 *
 * This is the fallback, the presentation overlay, and the id-stability floor.
 * Saved agent configs reference these ids, so entries are only ever ADDED here
 * — removing one silently blanks the picker for every agent using it.
 */
export const GROK_VOICE_OPTIONS: readonly GrokVoiceOption[] = [
  ...GROK_VOICE_ORIGINAL,
  ...GROK_VOICE_ADDED_2026_07,
];

/**
 * One voice as xAI's `GET /v1/tts/voices` describes it.
 *
 * Verified against the live endpoint on 2026-09-06 (not inferred): the envelope
 * is `{ voices: [...] }` and each entry carries exactly
 * `{ voice_id, name, language, gender }` — e.g.
 * `{"voice_id":"altair","name":"Altair","language":"multilingual","gender":"male"}`.
 *
 * That probe also settled the premise: xAI served **28** voices against the 26
 * hardcoded below. `aurora` and `liora` are missing from our list, and every one
 * of our 26 is still present upstream. So the drift this ticket describes had
 * already happened a third time, and nothing had reported it.
 *
 * `name` is the title-cased id, which is why `titleCaseVoiceId` is a faithful
 * fallback rather than an approximation.
 *
 * Every field but `voice_id` is optional here on purpose — a response shape is
 * not a contract we control, and the only field we cannot proceed without is
 * the id.
 */
export interface XaiVoiceEntry {
  voice_id: string;
  name?: string;
  language?: string;
  /**
   * Present upstream, deliberately unused. The original five carry a gender in
   * their label ("Eve (Female)"); the 21 added in #3899 do not, so the picker is
   * already mixed and using this would change 21 existing labels rather than
   * make things consistent. A follow-up could relabel all of them from this
   * field — that is a copy decision, not a plumbing one.
   */
  gender?: string;
}

/** Where a rendered roster came from — surfaced so a picker can be diagnosed. */
export type GrokVoiceRosterSource = 'xai' | 'fallback';

/**
 * `carina` → `Carina`. Used only for ids xAI has that we do not, so a voice
 * added upstream renders as something readable rather than a bare lowercase id.
 *
 * xAI's own `name` is preferred when it looks like a voice name; see
 * `mergeGrokVoiceRoster`.
 */
export function titleCaseVoiceId(id: string): string {
  return id.length === 0 ? id : id[0]!.toUpperCase() + id.slice(1);
}

/**
 * Merge a fetched xAI roster into the local overlay.
 *
 * The rule, and it is a fail-direction decision rather than a preference:
 *
 * **Union, never intersection.** Local entries first, in local order (so `eve`
 * stays first and the labelled originals keep their labels), then any id xAI
 * has that we do not, in xAI's order. A partial or filtered response from xAI
 * therefore cannot SHRINK the picker — which is the silent failure, because a
 * voice vanishing from a selector looks like a product decision. The opposite
 * case is loud and small: a voice xAI has retired stays offered and fails at
 * call time for the one agent that picked it.
 *
 * Ids failing `GROK_VOICE_ID_PATTERN` are dropped. Same reason as above — a
 * malformed id renders perfectly and fails at `session.update`.
 *
 * `language` is deliberately NOT filtered on. If that ever changes, a local
 * overlay id must survive the filter regardless.
 */
export function mergeGrokVoiceRoster(
  fetched: readonly XaiVoiceEntry[],
  overlay: readonly GrokVoiceOption[] = GROK_VOICE_OPTIONS,
): GrokVoiceOption[] {
  const merged: GrokVoiceOption[] = overlay.map((v) => ({ ...v }));
  const seen = new Set(merged.map((v) => v.id));

  for (const entry of fetched) {
    const id = typeof entry?.voice_id === 'string' ? entry.voice_id.trim().toLowerCase() : '';
    if (!GROK_VOICE_ID_PATTERN.test(id) || seen.has(id)) continue;
    const name = typeof entry.name === 'string' ? entry.name.trim() : '';
    merged.push({ id, label: name.length > 0 ? name : titleCaseVoiceId(id) });
    seen.add(id);
  }

  return merged;
}

/**
 * Guarantee the currently-selected voice is offered.
 *
 * The case this exists for: xAI serves a new voice `nova`, an operator selects
 * and saves it, and later a cold Lambda's fetch fails so the roster falls back
 * to the static 26 — which has no `nova`. The `<select>` then renders with no
 * matching option, i.e. BLANK, for that agent only. "Populated from the local
 * fallback" is technically satisfied and the picker is still broken.
 *
 * Serving the last good fetch narrows this window; it does not close it. So
 * every selector appends its own saved value when the roster lacks it.
 */
export function withSelectedVoice(
  options: readonly GrokVoiceOption[],
  selectedId: string | null | undefined,
): GrokVoiceOption[] {
  const id = typeof selectedId === 'string' ? selectedId.trim() : '';
  if (id.length === 0 || options.some((v) => v.id === id)) {
    return options.map((v) => ({ ...v }));
  }
  return [...options.map((v) => ({ ...v })), { id, label: titleCaseVoiceId(id) }];
}
